import { useDispatch, useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";
import Loader from "../Loader/Loader";
import queryString from "query-string";
import CommonPagination from "../CommonPagination/CommonPagination";
import { GET_PAGEMAPPING_DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import PageMappingSubHeader from "./PageMappingSubHeader";
import {
  getPageMappings
} from "../../../core/actionCreators/pageMappingActionCreator";
import PageMappingListItem from "./PageMappingListItem";
import { removeEmptyStringKeys } from "../../../utils/helper";

const EMPTY_FILTERS = {
  id: "",
  requestParam: "",
  layoutCodes: "",
  isWebView: "",
};

const FILTER_DEBOUNCE_MS = 600;

const INPUT_CLASS = "border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full";

const PageMappingList = () => {
  const dispatch = useDispatch();

  const { pageMappings, isLoading } = useSelector(
    (s: any) => s.pageMappingStore
  );

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any);
  const [pageNumber, setPageNumber] = useState(0);

  // Typing should settle before firing a request; paging should not wait.
  const isFilterChangeRef = useRef(false);

  useEffect(() => {
    const delay = isFilterChangeRef.current ? FILTER_DEBOUNCE_MS : 0;
    const timeout = setTimeout(() => {
      isFilterChangeRef.current = false;
      dispatch(getPageMappings(
        queryString.stringify(removeEmptyStringKeys({
          ...filters,
          pageNumber,
          pageSize: GET_PAGEMAPPING_DEFAULT_PAGE_SIZE,
        })),
        null
      ));
    }, delay);
    return () => clearTimeout(timeout);
  }, [filters, pageNumber, dispatch]);

  // A changed filter re-runs the search from the first page — keeping the old
  // page number would land the user on an empty tail of a shorter result set.
  const onFilterChange = (key: string, value: string) => {
    isFilterChangeRef.current = true;
    setFilters({ ...filters, [key]: value });
    setPageNumber(0);
  };

  const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key]);

  const clearFilters = () => {
    isFilterChangeRef.current = false;
    setFilters({ ...EMPTY_FILTERS });
    setPageNumber(0);
  };

  // CommonPagination counts pages from 1, the API counts them from 0.
  const onPageChange = (page: any) => {
    setPageNumber(Math.max(0, Number(page) - 1));
  };

  return (
    <>
      <PageMappingSubHeader />

      <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="w-[140px]">
          <label className="block text-[11px] text-gray-600 mb-1">ID</label>
          <input
            type="text"
            placeholder="Search ID"
            value={filters.id}
            onChange={(e) => onFilterChange("id", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="w-[280px]">
          <label className="block text-[11px] text-gray-600 mb-1">Request Param</label>
          <input
            type="text"
            placeholder="Search request param"
            value={filters.requestParam}
            onChange={(e) => onFilterChange("requestParam", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="w-[200px]">
          <label className="block text-[11px] text-gray-600 mb-1">Layout Code's</label>
          <input
            type="text"
            placeholder="Search layout code"
            value={filters.layoutCodes}
            onChange={(e) => onFilterChange("layoutCodes", e.target.value)}
            className={INPUT_CLASS}
          />
        </div>
        <div className="w-[140px]">
          <label className="block text-[11px] text-gray-600 mb-1">Status</label>
          <select
            value={filters.isWebView}
            onChange={(e) => onFilterChange("isWebView", e.target.value)}
            className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs"
          >
            <option value="">All</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </div>
        <button
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Clear Filters
        </button>
      </div>

      <div className="mt-2.5 campaign-list bg-white mb-[54px]">
        <table className="w-full">
          <thead className="bg-[#343434]">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[40%]">Request Param</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[25%]">Layout Code's</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[15%]">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-white w-[10%]">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="py-12">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Loader />
                    <span className="text-xs text-gray-500">Loading page mappings...</span>
                  </div>
                </td>
              </tr>
            ) : (
              <PageMappingListItem data={pageMappings?.data || []} />
            )}
          </tbody>
        </table>
      </div>
      {pageMappings?.data && (
        <CommonPagination
          data={pageMappings}
          PAGE_SIZE={GET_PAGEMAPPING_DEFAULT_PAGE_SIZE}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};
export default PageMappingList;
